import { type ComponentType, type FC, memo, useState } from 'react';

import { useTranslation } from '@crehana/i18n';
import Edit2 from '@crehana/icons/outline/Edit2';
import PriceTag from '@crehana/icons/outline/PriceTag';
import Trash2 from '@crehana/icons/outline/Trash2';
import type { IconProps } from '@crehana/icons/types';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { colors, IconButton, Tooltip } from '@crehana/ui';
import { notEmpty } from '@crehana/utils';
import { clsx } from 'clsx';

import { useAttributes } from '@/views/AdminDashboard/components/AttributesDrawer/hooks';

import { BoxCount, WrapperList } from './AttributeList.styles';
import {
  AttributeForm,
  AttributeTagForm,
  DeleteDialog,
  TagList,
} from './components';

interface Tag {
  originalId: string | null;
  name: string;
}
interface Attribute {
  originalId: string;
  name: string;
  tags: Tag[];
}

interface AttributesListProps {
  attribute: Attribute;
  step?: number;
  isDark?: boolean;
}

interface ActionIconProps {
  isDark?: boolean;
  tooltip: string;
  icon: ComponentType<React.PropsWithChildren<IconProps>>;
  cta: () => void;
}

const ActionIcon: FC<React.PropsWithChildren<ActionIconProps>> = ({
  isDark,
  tooltip,
  icon: Icon,
  cta,
}) => {
  return (
    <Tooltip
      label={tooltip}
      isDark={isDark}
      className={clsx(isDark && 'bg-white text-base-main')}
    >
      <IconButton
        className="h-20 :hover:text-secondary-dark"
        icon={{ component: Icon }}
        withoutBorders
        customSize={20}
        css={`
          color: ${colors.baseLighter};
          &:hover {
            color: ${isDark ? colors.secondaryMain : colors.primaryLight};
          }
        `}
        onClick={cta}
      />
    </Tooltip>
  );
};

const AttributeList: FC<React.PropsWithChildren<AttributesListProps>> = ({
  attribute,
  step,
  isDark,
}) => {
  const { t } = useTranslation();
  const { onClearAttribute } = useAttributes();

  const [editAttr, setEditAttr] = useState(false);
  const [elementToDelete, setElementToDelete] = useState<boolean>();

  const deleteAttribute = () => {
    if (attribute.originalId) {
      onClearAttribute && onClearAttribute({ id: attribute.originalId });
    }
    setElementToDelete(undefined);
  };

  const tags = attribute?.tags?.filter(notEmpty) || [];

  return (
    <WrapperList isDark={isDark} key={attribute.originalId}>
      {editAttr ? (
        <AttributeForm
          attribute={attribute}
          isDark={isDark}
          onClose={() => setEditAttr(false)}
        />
      ) : (
        <div className="flex flex-wrap w-full">
          <div className="flex w-full">
            <span
              className={`font-subtitle3 truncate ${
                isDark ? 'text-white' : 'text-base-dark'
              }`}
            >
              {attribute.name || ''}
            </span>
            {step !== 3 && (
              <div className="flex justify-between h-20 align-center flex-grow">
                {step === 2 && (
                  <BoxCount className="font-caption ml-12" isDark={isDark}>
                    <PriceTag
                      size={14}
                      color={colors.grayDark}
                      className="fill-current mr-4"
                    />
                    <span>{tags?.length || 0}</span>
                    <span className="font-body5 text-gray-dark ml-4">
                      {t('ATTR_COUNT_TAG_LABEL')}
                    </span>
                  </BoxCount>
                )}
                <div className="flex-grow" />
                <ActionIcon
                  icon={Edit2}
                  isDark={isDark}
                  cta={() => setEditAttr(true)}
                  tooltip={t('ATTR_BUTTON_EDIT_TOOLTIP')}
                />
                <ActionIcon
                  icon={Trash2}
                  isDark={isDark}
                  cta={() => setElementToDelete(true)}
                  tooltip={t('ATTR_BUTTON_TRASH_TOOLTIP')}
                />
              </div>
            )}
          </div>
          {step === 2 && (
            <>
              <AttributeTagForm
                attributeId={attribute.originalId}
                isDark={isDark}
              />
              <section className="flex w-full flex-wrap">
                {tags &&
                  tags.map((tag, i) => (
                    <TagList
                      // biome-ignore lint/suspicious/noArrayIndexKey: index as key is acceptable here
                      key={`attribute-tag-${i}`}
                      tag={tag}
                      showActions
                      isDark={isDark}
                      attributeId={attribute.originalId}
                    />
                  ))}
              </section>
            </>
          )}
          {step === 3 && (
            <section className="flex w-full flex-wrap">
              {tags &&
                tags.map(tag => (
                  <TagList
                    key={tag.originalId}
                    tag={tag}
                    isDark={isDark}
                    attributeId={attribute.originalId}
                  />
                ))}
            </section>
          )}
        </div>
      )}
      <DeleteDialog
        open={!!elementToDelete}
        isDark={isDark}
        onDismiss={() => setElementToDelete(undefined)}
        onConfirm={() => deleteAttribute()}
        title="ATTR_DELETE_DIALOG_TITLE"
        description="ATTR_DELETE_DIALOG_SUBTITLE"
        cancelButton="ATTR_DELETE_DIALOG_BUTTON_CANCEL"
        confirmButton="ATTR_DELETE_DIALOG_BUTTON_CONFIRM"
      />
    </WrapperList>
  );
};

export default memo(AttributeList);
